import { InputLabel, Modal, SelectChangeEvent } from "@mui/material";
import { useEffect, useState } from "react";

import { InviteUserRequest } from "@/@types/growthos-api/schema/openapi";
import { Notification } from "@/Components";
import { Box } from "@/Components/v2/atoms/Box";
import { Button } from "@/Components/v2/atoms/Button";
import { FormControl } from "@/Components/v2/atoms/FormControl";
import { MenuItem } from "@/Components/v2/atoms/MenuItem";
import { Select } from "@/Components/v2/atoms/Select";
import { TextField } from "@/Components/v2/atoms/TextField";
import { Typography } from "@/Components/v2/atoms/Typography";
import { validateEmail } from "@/Utils";

import { useAddUser } from "../api/addUser";

const AddUserModal = (props: { isAddUserOpen: boolean; handleAddUserModal: () => void }) => {
  const { isAddUserOpen, handleAddUserModal } = props;
  const addUserMutation = useAddUser();

  const [emailAddress, setEmailAddress] = useState("");
  const [isValidEmail, setIsValidEmail] = useState<boolean | null>(null);
  const [selectedRole, setSelectedRole] = useState("");
  const [isValidSelect, setIsValidSelect] = useState(true);

  const ROLE_OPTIONS = [
    { value: "Admin", label: "Admin" },
    { value: "Analyst", label: "Analyst" },
    { value: "Viewer", label: "Viewer" },
  ];

  const ROLE_DESCRIPTIONS = [
    {
      role: "Admin",
      content: ": Access Review, Scenario Builder, Overview pages, and Team Management.",
    },
    { role: "Analyst", content: ": Access Review, Scenario Builder, Overview pages." },
    { role: "Viewer", content: ": Access to Review pages only." },
  ];

  useEffect(() => {
    if (isAddUserOpen) {
      setEmailAddress("");
      setIsValidEmail(null);
      setSelectedRole("");
      setIsValidSelect(true);
    }
  }, [isAddUserOpen]);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailAddress(e.target.value);
  };

  const handleEmailBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsValidEmail(validateEmail(e.target.value));
  };

  const handleRoleChange = (e: SelectChangeEvent<string>) => {
    setSelectedRole(e.target.value);
    setIsValidSelect(true);
  };

  const onSave = () => {
    if (!isValidEmail) {
      setIsValidEmail(null);
      Notification("warning", "Invalid email address.");
      return;
    }
    if (!["Admin", "Viewer", "Analyst"].includes(selectedRole)) {
      Notification("warning", "Please select user role.");
      setIsValidSelect(false);
      return;
    }

    addUserMutation.mutate(
      {
        email: emailAddress,
        role: selectedRole as InviteUserRequest["role"],
      },
      {
        onSuccess: () => {
          handleAddUserModal();
        },
      }
    );
  };

  return (
    <Modal
      open={isAddUserOpen}
      onClose={handleAddUserModal}
      aria-labelledby="add-user-modal-title"
      aria-describedby="add-user-modal-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "490px",
          maxWidth: "90vw",
          maxHeight: "90vh",
          overflow: "auto",
          bgcolor: "background.paper",
          borderRadius: 1,
          boxShadow: 24,
          p: 3,
        }}
      >
        <Typography variant="h6" component="h2" sx={{ mb: 2, fontWeight: 500 }}>
          Add User
        </Typography>

        <Typography variant="body2" sx={{ mb: 3, fontSize: "12px", lineHeight: 1.4 }}>
          Enter the new user&apos;s email, and select the areas of the site you&apos;d like them to
          have access too. We&apos;ll send an invite email to them so they can set up their account.
        </Typography>

        {/* Email Input */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            type="email"
            label="Email"
            value={emailAddress}
            onChange={handleEmailChange}
            onBlur={handleEmailBlur}
            error={isValidEmail === false}
            helperText={isValidEmail === false ? "Invalid email address" : ""}
            size="small"
          />
        </Box>

        {/* Role Selection */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, fontSize: "12px" }}>
            Select User Role
          </Typography>

          {ROLE_DESCRIPTIONS.map((item, index) => (
            <Typography key={index} variant="body2" sx={{ mb: 0.5, fontSize: "10px" }}>
              <Box component="span" sx={{ fontWeight: 600 }}>
                {item.role}
              </Box>
              {item.content}
            </Typography>
          ))}

          <FormControl fullWidth size="small" sx={{ mt: 2 }}>
            <InputLabel>Select User Role</InputLabel>
            <Select
              value={selectedRole}
              onChange={handleRoleChange}
              label="Select User Role"
              error={!isValidSelect}
            >
              {ROLE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button variant="outlined" onClick={handleAddUserModal} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={onSave} disabled={addUserMutation.isPending}>
            {addUserMutation.isPending ? "Sending..." : "Send Invite"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddUserModal;
